import { Box, Menu, MenuDivider, MenuItem, MenuList } from "@caspeco/casper-ui-library";
import { useEffect, useRef, useState } from "react";
import { useFilterSettings } from "../../hooks/use-filter-settings";
import { useUserContext } from "../../hooks/use-user-context";
import { FilterSettings as FilterSettingsModel } from "../../models/schedule/filter-settings";
import { useTranslation } from "react-i18next";
import { FilterSettingsRowSwitch } from "./filter-settings-row";
import LocationFilter from "./location-filter";
import { useLocation } from "react-router";
import { FilterIconMenuButton } from "./filter-icon";
import { createPortal } from "react-dom";

const FilterSettings = () => {
	const [filterSettings, setFilterSettings] = useFilterSettings();
	const { systems } = useUserContext();
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const anchorRef = useRef<HTMLDivElement>(null);
	const [edgeOffset, setEdgeOffset] = useState(0);

	useEffect(() => {
		const anchor = anchorRef.current;
		const header = anchor?.closest<HTMLElement>("#page-header");
		if (!anchor || !header) return;
		setEdgeOffset(header.getBoundingClientRect().right - anchor.getBoundingClientRect().right);
	}, []);

	const noSystemsDisabled = filterSettings.disabledSystems.length === 0;
	return (
		<Box ref={anchorRef}>
			<Menu closeOnSelect={false} placement="bottom-end" offset={[edgeOffset, 0]}>
				{({ isOpen }) => (
					<>
						{isOpen &&
							createPortal(<div style={{ position: "fixed", inset: 0, zIndex: 999 }} />, document.body)}
						<FilterIconMenuButton activeFilter={filterSettings.hasActiveFilterForLocation(pathname)} />
						<MenuList>
							<LocationFilter />
							{systems.length > 1 && (
								<>
									<MenuDivider title={t("filterSettings_employer")} />
									<FilterSettingsRowSwitch
										name={t("filterSettings_showAll")}
										isChecked={noSystemsDisabled}
										setFunction={() =>
											setFilterSettings(
												"disabledSystems",
												noSystemsDisabled ? systems.map((s) => s.name) : [],
											)
										}
										data-testid="filter-show-all-systems-toggle"
									/>
									{systems?.map((system) => {
										const isChecked = !filterSettings.disabledSystems.includes(system.name);
										const newDisabledSystemsValue = isChecked
											? filterSettings.disabledSystems.concat([system.name])
											: filterSettings.disabledSystems.filter((s) => s !== system.name);
										return (
											<FilterSettingsRowSwitch
												key={system.name}
												name={system.commonName}
												isChecked={isChecked}
												setFunction={() => setFilterSettings("disabledSystems", newDisabledSystemsValue)}
												data-testid={`filter-system-toggle-${system.name}`}
											/>
										);
									})}
								</>
							)}

							<MenuDivider />
							<MenuItem
								value="reset-filters"
								onClick={() => setFilterSettings(undefined, new FilterSettingsModel({}))}
								isDisabled={!filterSettings.hasFilterActive}
								data-testid="filter-reset-all"
							>
								{t("filterSettings_resetFilters")}
							</MenuItem>
						</MenuList>
					</>
				)}
			</Menu>
		</Box>
	);
};

export default FilterSettings;
